/**
 * 单音高检测：时域归一化自相关 + 抛物线插值。
 * 取“85% 峰值阈值内最小的滞后周期”以避免八度误判。
 */
export const PITCH_MIN_FREQ = 55
export const PITCH_MAX_FREQ = 1600
export const PITCH_RMS_SILENCE = 0.006
export const PITCH_CLARITY_GATE = 0.82

export type PitchDetectResult = {
	freq: number
	clarity: number
	rms: number
}

export function detectPitch(buf: Float32Array, sampleRate: number): PitchDetectResult | null {
	const n = buf.length
	let rms = 0
	for(let i = 0; i < n; i++) rms += buf[i] * buf[i]
	rms = Math.sqrt(rms / n)
	if(rms < PITCH_RMS_SILENCE) return null

	const minLag = Math.floor(sampleRate / PITCH_MAX_FREQ)
	const maxLag = Math.min(Math.floor(sampleRate / PITCH_MIN_FREQ), n - 1)
	if(maxLag <= minLag) return null

	const sig = new Float64Array(n)
	let mean = 0
	for(let i = 0; i < n; i++) mean += buf[i]
	mean /= n
	let energy = 0
	for(let i = 0; i < n; i++) {
		sig[i] = buf[i] - mean
		energy += sig[i] * sig[i]
	}

	const corrs = new Float64Array(maxLag + 1)
	let best = 0
	for(let lag = minLag; lag <= maxLag; lag++) {
		let corr = 0
		for(let i = 0; i < n - lag; i++) corr += sig[i] * sig[i + lag]
		corrs[lag] = corr / energy
		if(corrs[lag] > best) best = corrs[lag]
	}
	if(best < PITCH_CLARITY_GATE) return null

	let lag = minLag
	for(; lag <= maxLag; lag++) {
		if(corrs[lag] >= best * 0.85 && corrs[lag] >= corrs[lag - 1] && corrs[lag] >= corrs[lag + 1]) {
			break
		}
	}
	if(lag > maxLag) return null

	const a = corrs[lag - 1], b = corrs[lag], c = corrs[lag + 1]
	const denom = 2 * b - a - c
	const shift = denom !== 0 ? (c - a) / (2 * denom) : 0
	const freq = sampleRate / (lag + shift)
	if(freq < PITCH_MIN_FREQ || freq > PITCH_MAX_FREQ) return null
	return { freq, clarity: best, rms }
}

export function freqToMidi(freq: number) {
	return Math.round(69 + 12 * Math.log2(freq / 440))
}

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B']
export function midiToName(midi: number) {
	return NOTE_NAMES[((midi % 12) + 12) % 12] + (Math.floor(midi / 12) - 1)
}
